import { _ } from "../../polyglot/polyglot.js";
import { widget } from "@foretagsplatsen/widgetjs";
import userInfoFormWidget from "./userInfoFormWidget.js";

/**
 * Widget to display basic info (name, username, email, phone number) about a user.
 *
 * @param {user} [spec.editedUser] - User about whom to display info
 * @param {user} spec.currentUser - User currently logged in
 * @param {company} [spec.company] - The current company if any
 * @param {function} spec.deleteUserCallback - Function called when `currentUser` click the delete button
 * @param {function} spec.promoteUserCallback - Function called when `currentUser` click the promote button
 * @param {function} spec.demoteUserCallback - Function called when `currentUser` click the demote button
 * @param {function} spec.updatePhoneCallback - Function called when `currentUser` click the update phone button
 * @param {function} spec.resendInvitationCallback - Function called when `currentUser` click the resend invitation button
 * @param {boolean} spec.showAAAUserEditionButtons - Allow the rendering of Edit info and Security
 * settings.
 */
export default widget.subclass((that, my) => {
	my.initialize = function ({
		currentUser,
		editedUser,
		company,
		deleteUserCallback,
		promoteUserCallback,
		demoteUserCallback,
		updatePhoneCallback,
		resendInvitationCallback,
		showAAAUserEditionButtons = true,
	}) {
		my.super(...arguments);
		my.currentUser = currentUser;
		my.editedUser = editedUser;
		my.company = company;
		my.deleteUserCallback = deleteUserCallback;
		my.promoteUserCallback = promoteUserCallback;
		my.demoteUserCallback = demoteUserCallback;
		my.updatePhoneCallback = updatePhoneCallback;
		my.resendInvitationCallback = resendInvitationCallback;
		my.showAAAUserEditionButtons = showAAAUserEditionButtons;
	};

	that.setEditedUser = function (editedUser) {
		if (editedUser === my.editedUser) {
			return;
		}

		my.editedUser = editedUser;
		that.update();
	};

	that.renderContentOn = function (html) {
		if (!my.editedUser) {
			return;
		}

		html.render(
			userInfoFormWidget({
				user: my.editedUser,
				company: my.company,
				updatePhoneCallback:
					my.canEditUserInfo() && my.updatePhoneCallback,
			}),
		);

		html.div(
			{ class: "button-sequence text-right" },
			my.resendInvitationButtonRenderer(),
			my.editUserInfoButtonRenderer(),
			my.securitySettingsButtonRenderer(),
			my.demoteButtonRenderer(),
			my.promoteButtonRenderer(),
			my.deleteButtonRenderer(),
		);
	};

	my.buttonRenderer = function ({
		showButtonPredicate,
		cssClass,
		action,
		label,
		dataTest,
	}) {
		return (html) => {
			if (!showButtonPredicate()) {
				return;
			}

			html.a(
				{
					klass: ["btn", cssClass],
					"data-test": dataTest,
					click: action.bind(null, my.editedUser),
				},
				label,
			);
		};
	};

	my.externalLinkButtonRenderer = function ({
		showButtonPredicate,
		dataTest = "",
		cssClass,
		link,
		label,
	}) {
		return (html) => {
			if (!showButtonPredicate()) {
				return;
			}

			html.a(
				{
					klass: ["btn", cssClass],
					"data-test": dataTest,
					href: link,
					target: "_blank",
				},
				html.i({ klass: "fas fa-external-link-alt" }),
				html.span(` ${label}`),
			);
		};
	};

	my.canResendInvitation = function () {
		return my.editedUser && my.editedUser.hasPendingInvitation();
	};

	my.canPromoteUser = function () {
		return my.editedUser && my.currentUser.canPromote(my.editedUser);
	};

	my.canDemoteUser = function () {
		return my.editedUser && my.currentUser.canDemote(my.editedUser);
	};

	my.canDeleteUser = function () {
		return my.editedUser && my.currentUser.canDeleteUser(my.editedUser);
	};

	my.canEditUserInfo = function () {
		return (
			my.editedUser && my.currentUser.getId() === my.editedUser.getId()
		);
	};

	my.shouldShowAAAUserEditionButtons = function () {
		return my.showAAAUserEditionButtons && my.canEditUserInfo();
	};

	my.resendInvitationButtonRenderer = function () {
		return my.buttonRenderer({
			showButtonPredicate: my.canResendInvitation,
			cssClass: "btn-primary",
			action: my.resendInvitationCallback,
			label: _("Resend Invitation"),
		});
	};

	my.demoteButtonRenderer = function () {
		return my.buttonRenderer({
			showButtonPredicate: my.canDemoteUser,
			cssClass: "btn-danger",
			action: my.demoteUserCallback,
			label: _("Demote"),
		});
	};

	my.promoteButtonRenderer = function () {
		return my.buttonRenderer({
			showButtonPredicate: my.canPromoteUser,
			cssClass: "btn-primary",
			action: my.promoteUserCallback,
			label: _("Promote"),
		});
	};

	my.deleteButtonRenderer = function () {
		if (!my.deleteUserCallback) {
			return () => {};
		}

		return my.buttonRenderer({
			showButtonPredicate: my.canDeleteUser,
			cssClass: "btn-danger",
			action: my.deleteUser,
			label: _("Delete"),
			dataTest: "delete-user",
		});
	};

	my.editUserInfoButtonRenderer = function () {
		return my.externalLinkButtonRenderer({
			showButtonPredicate: my.shouldShowAAAUserEditionButtons,
			cssClass: "btn-primary",
			dataTest: "edit-user",
			link: "/External/Authenticate/UserInfo",
			label: _("Edit info"),
		});
	};

	my.securitySettingsButtonRenderer = function () {
		return my.externalLinkButtonRenderer({
			showButtonPredicate: my.shouldShowAAAUserEditionButtons,
			cssClass: "btn-danger",
			link: "/External/Authenticate/UserSecuritySettings",
			label: _("Security settings"),
		});
	};

	my.deleteUser = function () {
		my.confirmDeletion({
			name: my.editedUser.getName(),
			callback: () => my.deleteUserCallback(my.editedUser),
		});
	};
});
